import { AccessLevel } from "@/api/williams/users";
import { ExpandableCard } from "../../components";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SpinButton } from "@/components/form/SpinButton";
import { usePermissions } from "../../users/hooks";
import { useRevokeProcess, useWelderProcessStatus } from "../hooks";
import { useState } from "react";
import { WelderProcess } from "@/api/williams/welders";

type ExpandedProps = {
  status: string;
  process: WelderProcess;
  welderId: string;
  onFieldChanged: (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => void;
};

export function ProcessStatus({ welderId }: { welderId: string }) {
  const processes = useWelderProcessStatus(welderId);
  const total = processes.data?.length ?? 0;

  return (
    <ExpandableCard caption={`Process Status (${total})`}>
      {processes.isLoading ? (
        <p>Loading...</p>
      ) : processes.isError ? (
        <p>An unknown error occurred.</p>
      ) : processes.isSuccess && processes.data.length > 0 ? (
        <ProcessTable welderId={welderId} data={processes.data} />
      ) : (
        <p>No processes found.</p>
      )}
    </ExpandableCard>
  );
}

function ProcessTable({ welderId, data }: { welderId: string; data: WelderProcess[] }) {
  return (
    <table className="table table-sm table-borderless mb-0">
      <thead>
        <tr>
          <th className="col col-md-3">Process-technique</th>
          <th className="col-3 d-none d-md-table-cell text-center">Status</th>
          <th className="col-3 d-none d-md-table-cell text-center">Revoke Reason</th>
          <th className="col-3 d-none d-md-table-cell text-center">Retest Date</th>
          <th className="col-auto" />
        </tr>
      </thead>
      <tbody>
        {data.map((process) => (
          <ProcessRow key={`${process.processId}-${process.status}`} welderId={welderId} row={process} />
        ))}
      </tbody>
    </table>
  );
}

function ProcessRow({ welderId, row }: { welderId: string; row: WelderProcess }) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [process, setProcess] = useState(row);

  const revoke = useRevokeProcess(welderId);
  const permissions = usePermissions();
  const allowOverride = permissions.data && permissions.data.accessLevel >= AccessLevel.WeldingInspector;

  function handleFieldChanged(e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) {
    setProcess((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  return (
    <>
      <tr>
        <td>
          {process.processName}-{process.processType} ({process.standard})
        </td>
        <td className="d-none d-md-table-cell">
          <select
            className="form-select"
            name="status"
            value={process.status}
            onChange={handleFieldChanged}
            disabled={!allowOverride || process.revoked}
          >
            <option>{row.status}</option>
            <option>Revoked</option>
          </select>
        </td>
        <td className="d-none d-md-table-cell">
          <input
            type="text"
            className="form-control"
            name="revokedReason"
            value={process.revokedReason ?? ""}
            onChange={handleFieldChanged}
            disabled={!allowOverride || process.status !== "Revoked" || process.revoked}
          />
        </td>
        <td className="d-none d-md-table-cell">
          <input
            type="date"
            className="form-control w-100"
            name="retestDate"
            value={process.retestDate ?? ""}
            onChange={handleFieldChanged}
            min={new Date().toJSON().slice(0, 10)}
            disabled={!allowOverride || process.status !== "Revoked"}
          />
        </td>
        <td className="text-end text-nowrap">
          <SpinButton
            loading={revoke.isLoading}
            className="btn btn-default d-none d-md-inline me-2"
            onClick={() => revoke.mutate(process)}
            disabled={process.status !== "Revoked" || !process.revokedReason || !process.retestDate || !allowOverride}
          >
            Save
          </SpinButton>
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="d-inline d-md-none me-1"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td className="d-md-none" colSpan={2}>
            <ExpandedDetails
              status={row.status}
              process={process}
              welderId={welderId}
              onFieldChanged={handleFieldChanged}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ status, process, welderId, onFieldChanged }: ExpandedProps) {
  const revoke = useRevokeProcess(welderId);
  const permissions = usePermissions();

  const allowOverride = permissions.data && permissions.data.accessLevel >= AccessLevel.WeldingInspector;

  return (
    <div className="ms-1">
      <div className="bg-light p-1">Details</div>
      <div className="p-1">
        <dl>
          <dt>Status</dt>
          <dd>
            <select
              className="form-select"
              name="status"
              value={process.status}
              onChange={onFieldChanged}
              disabled={!allowOverride || process.revoked}
            >
              <option>{status}</option>
              <option>Revoked</option>
            </select>
          </dd>
          <dt>Revoke Reason</dt>
          <dd>
            <input
              type="text"
              className="form-control"
              name="revokedReason"
              value={process.revokedReason ?? ""}
              onChange={onFieldChanged}
              disabled={!allowOverride || process.status !== "Revoked" || process.revoked}
            />
          </dd>
          <dt>Retest Date</dt>
          <dd>
            <input
              type="date"
              className="form-control w-100"
              name="retestDate"
              value={process.retestDate ?? ""}
              onChange={onFieldChanged}
              min={new Date().toJSON().slice(0, 10)}
              disabled={!allowOverride || process.status !== "Revoked"}
            />
          </dd>
          <dd>
            <SpinButton
              loading={revoke.isLoading}
              className="btn btn-default me-2"
              onClick={() => revoke.mutate(process)}
              disabled={process.status !== "Revoked" || !process.revokedReason || !process.retestDate || !allowOverride}
            >
              Update
            </SpinButton>
          </dd>
        </dl>
      </div>
    </div>
  );
}
